import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { withTranslation } from "react-i18next";
import checkIcon from "assets/images/ico-check.svg";
import tabStyles from "../EventsShiftsTab.module.scss";

const EVENT_SHIFT_VIEW = {
  MONTHLY: "2",
  DAILY: "3",
  WEEKLY: "4",
};

/**
 * Resolves copy/clear/save-template menu label keys for the active calendar view.
 * @param {string} calendarViewId
 * @returns {{ copyPrevious: string, clear: string, saveAsTemplate: string }}
 */
export function getPeriodTemplateActionLabelKeys(calendarViewId) {
  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    return {
      copyPrevious: "scheduler.copyPreviousDay",
      clear: "scheduler.clearDay",
      saveAsTemplate: "scheduler.saveDayAsTemplate",
    };
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    return {
      copyPrevious: "scheduler.copyPreviousMonthly",
      clear: "scheduler.clearMonthly",
      saveAsTemplate: "scheduler.saveMonthlyAsTemplate",
    };
  }

  return {
    copyPrevious: "scheduler.copyPreviousWeek",
    clear: "scheduler.clearWeek",
    saveAsTemplate: "scheduler.saveWeekAsTemplate",
  };
}

function EventsShiftsWeeklyStaffToolbar({
  t,
  calendarViewId,
  showViewOptions = true,
  showSortBy = false,
  weeklyLayoutOption,
  onWeeklyLayoutChange,
  weeklyLayoutOptions,
  sortStaffBy,
  onSortStaffByChange,
  sortStaffOptions = [],
  bulkSelectMode,
  onBulkSelectModeChange,
  selectAllChecked,
  onSelectAllChange,
  hasSelection,
  canPublishGlobal,
  canPublishSelected,
  canDeleteSelected,
  isPublishing,
  isDeleting,
  onCopyPreviousWeek,
  onClearWeek,
  onSaveWeekAsTemplate,
  onAddFromSavedTemplate,
  onPublishSelected,
  onUnpublishSelected,
  onDeleteSelected,
  onPublishWeek,
  canClearPeriod = true,
  canCopyPrevious = true,
  canSaveAsTemplate = true,
  canApplyTemplate = true,
  isClearing = false,
  isCopying = false,
  isSavingTemplate = false,
  isApplyingTemplate = false,
}) {
  const selectionActionsDisabled = !bulkSelectMode || !hasSelection;
  const publishSelectedDisabled = !canPublishSelected || isPublishing;
  /** Global Publish always publishes all eligible drafts; selection is ignored. */
  const publishButtonDisabled = isPublishing || !canPublishGlobal;
  const deleteSelectedDisabled =
    !canDeleteSelected || selectionActionsDisabled || isDeleting;
  const clearPeriodDisabled = !canClearPeriod || isClearing;
  const copyPreviousDisabled = !canCopyPrevious || isCopying;
  const saveAsTemplateDisabled = !canSaveAsTemplate || isSavingTemplate;
  const applyTemplateDisabled = !canApplyTemplate || isApplyingTemplate;
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isViewOptionsOpen, setIsViewOptionsOpen] = useState(false);
  const [isSortByOpen, setIsSortByOpen] = useState(false);
  const toolbarRef = useRef(null);
  const periodActionLabels = useMemo(
    () => getPeriodTemplateActionLabelKeys(calendarViewId),
    [calendarViewId],
  );

  const closeMenus = useCallback(() => {
    setIsActionsOpen(false);
    setIsViewOptionsOpen(false);
    setIsSortByOpen(false);
  }, []);

  const handleOutsideClick = useCallback(
    (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        closeMenus();
      }
    },
    [closeMenus],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleOutsideClick]);

  useEffect(() => {
    if (!showViewOptions) {
      setIsViewOptionsOpen(false);
    }
  }, [showViewOptions]);

  useEffect(() => {
    if (!showSortBy) {
      setIsSortByOpen(false);
    }
  }, [showSortBy]);

  const runMenuAction = (handler, disabled) => (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (disabled) return;
    closeMenus();
    handler?.();
  };

  const handleViewOptionSelect = (layoutId) => {
    onWeeklyLayoutChange(layoutId);
    setIsViewOptionsOpen(false);
  };

  const handleSortOptionSelect = (sortId) => {
    onSortStaffByChange?.(sortId);
    setIsSortByOpen(false);
  };

  return (
    <div className={tabStyles["weekly-staff-toolbar"]} ref={toolbarRef}>
      <div className={tabStyles["weekly-staff-toolbar-row"]}>
        <div className={tabStyles["weekly-staff-toolbar-left"]}>
          <div className={tabStyles["weekly-staff-bulk-controls"]}>
            <div className="ch-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={bulkSelectMode}
                  onChange={(e) => onBulkSelectModeChange(e.target.checked)}
                />
                <span>{t("scheduler.bulkSelect")}</span>
              </label>
            </div>
            {bulkSelectMode && (
              <div className="ch-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={(e) => onSelectAllChange(e.target.checked)}
                  />
                  <span>{t("staff.selectAll")}</span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className={tabStyles["weekly-staff-toolbar-right"]}>
        {showViewOptions && (
          <div className={tabStyles["weekly-view-options-dropdown"]}>
            <button
              type="button"
              className={tabStyles["weekly-view-options-toggle"]}
              onClick={() => {
                setIsViewOptionsOpen((prev) => !prev);
                setIsActionsOpen(false);
                setIsSortByOpen(false);
              }}
              aria-expanded={isViewOptionsOpen}
              aria-haspopup="listbox"
            >
              <span>{t("scheduler.viewOptions")}</span>
              <img
                src={require("assets/images/caret.svg").default}
                alt=""
                className={tabStyles["weekly-view-options-caret"]}
              />
            </button>
            {isViewOptionsOpen && (
              <div className={tabStyles["weekly-toolbar-menu"]} role="listbox">
                {weeklyLayoutOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={tabStyles["weekly-toolbar-menu-item"]}
                    onClick={() => handleViewOptionSelect(option.id)}
                  >
                    {weeklyLayoutOption === option.id ? (
                      <img src={checkIcon} alt="" className="mr-2" />
                    ) : (
                      <span
                        className={tabStyles["weekly-toolbar-menu-spacer"]}
                      />
                    )}
                    {option.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {showSortBy && (
          <div className={tabStyles["weekly-sort-by-dropdown"]}>
            <button
              type="button"
              className={tabStyles["weekly-view-options-toggle"]}
              onClick={() => {
                setIsSortByOpen((prev) => !prev);
                setIsViewOptionsOpen(false);
                setIsActionsOpen(false);
              }}
              aria-expanded={isSortByOpen}
              aria-haspopup="listbox"
            >
              <span>{t("scheduler.sortBy")}</span>
              <img
                src={require("assets/images/caret.svg").default}
                alt=""
                className={tabStyles["weekly-view-options-caret"]}
              />
            </button>
            {isSortByOpen && (
              <div className={tabStyles["weekly-toolbar-menu"]} role="listbox">
                {sortStaffOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={tabStyles["weekly-toolbar-menu-item"]}
                    onClick={() => handleSortOptionSelect(option.id)}
                  >
                    {sortStaffBy === option.id ? (
                      <img src={checkIcon} alt="" className="mr-2" />
                    ) : (
                      <span
                        className={tabStyles["weekly-toolbar-menu-spacer"]}
                      />
                    )}
                    {option.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className={tabStyles["weekly-policy-actions-dropdown"]}>
          <button
            type="button"
            className={tabStyles["weekly-policy-actions-trigger"]}
            onClick={() => {
              setIsActionsOpen((prev) => !prev);
              setIsViewOptionsOpen(false);
              setIsSortByOpen(false);
            }}
            aria-expanded={isActionsOpen}
            aria-haspopup="menu"
          >
            <span className="ico">
              <img
                src={require("assets/images/dots-icon.svg").default}
                alt=""
              />
            </span>
          </button>

          {isActionsOpen && (
            <div className={tabStyles["weekly-toolbar-menu"]} role="menu">
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={copyPreviousDisabled}
                onClick={runMenuAction(
                  onCopyPreviousWeek,
                  copyPreviousDisabled,
                )}
              >
                {t(periodActionLabels.copyPrevious)}
              </button>
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={clearPeriodDisabled}
                onClick={runMenuAction(onClearWeek, clearPeriodDisabled)}
              >
                {t(periodActionLabels.clear)}
              </button>
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={saveAsTemplateDisabled}
                onClick={runMenuAction(
                  onSaveWeekAsTemplate,
                  saveAsTemplateDisabled,
                )}
              >
                {t(periodActionLabels.saveAsTemplate)}
              </button>
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={applyTemplateDisabled}
                onClick={runMenuAction(
                  onAddFromSavedTemplate,
                  applyTemplateDisabled,
                )}
              >
                {t("scheduler.addFromSavedTemplate")}
              </button>
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={publishSelectedDisabled}
                onClick={runMenuAction(
                  onPublishSelected,
                  publishSelectedDisabled,
                )}
              >
                {t("scheduler.publishSelected")}
              </button>
              {/* Comment for future sprint */}
              {/* <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={selectionActionsDisabled}
                onClick={runMenuAction(
                  onUnpublishSelected,
                  selectionActionsDisabled,
                )}
              >
                {t("scheduler.unpublishSelected")}
              </button> */}
              <button
                type="button"
                className={tabStyles["weekly-toolbar-menu-item"]}
                disabled={deleteSelectedDisabled}
                onClick={runMenuAction(
                  onDeleteSelected,
                  deleteSelectedDisabled,
                )}
              >
                {t("scheduler.deleteSelected")}
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          className={`button button-round button-shadow ${tabStyles["btn-nu"]}`}
          disabled={publishButtonDisabled}
          style={{ border: "0px !important" }}
          onClick={onPublishWeek}
        >
          {t("scheduler.publish")}
        </button>
        </div>
      </div>

      <ul className={tabStyles["weekly-staff-legend"]}>
        <li>
          <span
            className={`${tabStyles["legend-swatch"]} ${tabStyles["legend-published"]}`}
          />
          <span>{t("scheduler.publishedEvent")}</span>
        </li>
        <li>
          <span
            className={`${tabStyles["legend-swatch"]} ${tabStyles["legend-draft"]}`}
          />
          <span>{t("scheduler.isDraft")}</span>
        </li>
        {/* Comment for future sprint */}
        {/* <li>
          <span
            className={`${tabStyles["legend-swatch"]} ${tabStyles["legend-unavailable"]}`}
          />
          <span>{t("scheduler.unavailable")}</span>
        </li> */}
      </ul>
    </div>
  );
}

export default withTranslation()(EventsShiftsWeeklyStaffToolbar);
